import React, { createContext, useCallback, useContext, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";

type BackHandler = (() => void) | null;

interface CheckoutLayoutContextType {
  setBackHandler: (handler: BackHandler) => void;
}

const CheckoutLayoutContext = createContext<CheckoutLayoutContextType | null>(null);

export function useCheckoutLayout() {
  return useContext(CheckoutLayoutContext);
}

const CheckoutLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isConfirmation = location.pathname.endsWith("/confirmation");
  const [backHandler, setBackHandlerState] = useState<BackHandler>(null);

  const setBackHandler = useCallback((handler: BackHandler) => {
    setBackHandlerState(() => handler);
  }, []);

  function handleBack() {
    if (backHandler) {
      backHandler();
    } else {
      navigate(-1);
    }
  }

  return (
    <CheckoutLayoutContext.Provider value={{ setBackHandler }}>
      <div className="flex h-screen w-full flex-col bg-background text-foreground">
        {/* Slim top bar */}
        <header className="flex items-center justify-between border-b border-gray-200 dark:border-slate-700 px-4 sm:px-6 h-14 flex-shrink-0 bg-white dark:bg-slate-900">
          {isConfirmation ? (
            <button
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => navigate("/event-registration/events")}
              type="button"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back to Events</span>
            </button>
          ) : (
            <button
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              onClick={handleBack}
              type="button"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back</span>
            </button>
          )}
          {!isConfirmation && (
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Lock className="h-3.5 w-3.5" />
              <span>Secure Checkout</span>
            </div>
          )}
        </header>

        {/* Content area */}
        <main className="flex-1 overflow-y-auto bg-gray-50 dark:bg-slate-950">
          <Outlet />
        </main>
      </div>
    </CheckoutLayoutContext.Provider>
  );
};

export default CheckoutLayout;
